import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  type OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import type {
  PopulationBasicInfo,
  PopulationType,
} from '@bizzdesign/data-access-populations';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  DefaultDialogSize,
  type DialogProperties,
} from '@bizzdesign/shared/models';
import { DialogComponent } from '@bizzdesign/ui-components';
import { TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { map, type Observable, of } from 'rxjs';

@Component({
  selector: 'bd-create-population-modal',
  imports: [
    AsyncPipe,
    SharedI18nModule,
    ButtonModule,
    FormsModule,
    TooltipModule,
    InputGroupModule,
    DialogComponent,
    InputTextModule,
  ],
  templateUrl: './create-population-modal.component.html',
  styleUrl: './create-population-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatePopulationModalComponent implements OnInit {
  private translocoService = inject(TranslocoService);

  private _visible = false;
  @Input() dataForPopulationLoading = false;
  @Input({ required: true }) populationType: PopulationType | undefined;
  @Input() numberOfFilters: number | undefined;
  @Input() numberOfSelectedObjects: number | undefined;
  @Input() showWarningMessageForDynamicPopulation = false;
  @Input() set visible(visible: boolean) {
    this._visible = visible;
    if (visible) {
      this.populationNameInputDisabled = false;
      this.populationName = undefined;
    }
  }
  get visible(): boolean {
    return this._visible;
  }

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saveAsPopulation = new EventEmitter<PopulationBasicInfo>();
  @Output() cancelSaveAsPopulation = new EventEmitter();

  dialogProperties$: Observable<DialogProperties> = of();
  populationName: string | undefined;
  populationNameInputDisabled = false;

  ngOnInit(): void {
    this.dialogProperties$ = this.translocoService
      .selectTranslate(
        'saveAsPopulationModal.modalTitle',
        undefined,
        'featureSearch',
      )
      .pipe(
        map((title) => ({
          defaultHeader: {
            headerTitle: title,
          },
          defaultSize: DefaultDialogSize.SMALL,
        })),
      );
  }

  cancel() {
    this.populationName = undefined;
    this.setVisibility(false);
    this.cancelSaveAsPopulation.emit();
  }

  save() {
    if (this.populationName) {
      this.saveAsPopulation.emit({
        name: this.populationName!,
        type: this.populationType!,
      });
      this.populationNameInputDisabled = true;
    }
  }

  setVisibility(visible: boolean) {
    this.visible = visible;
    this.visibleChange.emit(this.visible);
  }
}
